import { QMenuButton } from '@qualio/ui-components';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DocumentVersion } from '../../../api/model/document';
import { getVersionsToDisplay } from '../DocumentOverview/Buttons/index';
import {
  sortVersions,
  versionIsGreaterThan,
} from '../DocumentOverview/RenderLogic';
import { parseVersion } from './util';

export type VersionSelectOperatorVariant = 'LHS' | 'RHS';

interface VersionSelectProps {
  latestVersion: string;
  version: string | null | undefined;
  handleOptionChange: (option: string) => void;
  allVersions: DocumentVersion[];
  variant: VersionSelectOperatorVariant;
  LHSVersion?: string | null;
}

export const VersionSelect = ({
  latestVersion,
  version,
  handleOptionChange,
  allVersions,
  variant,
  LHSVersion,
}: VersionSelectProps) => {
  const { t } = useTranslation();

  const getVersionsToCompareAgainst = useCallback(
    (versions: DocumentVersion[]) => {
      if (!LHSVersion) {
        return versions;
      }

      return versions.filter((ver) => {
        const isVersionOlderThanDocVersion = !versionIsGreaterThan(
          ver,
          parseVersion(LHSVersion) as unknown as DocumentVersion,
        );

        return isVersionOlderThanDocVersion && ver.version !== LHSVersion;
      });
    },
    [LHSVersion],
  );

  const sortedVersions = useMemo(
    () => (allVersions ? sortVersions(allVersions) : []),
    [allVersions],
  );

  const versionsToDisplay = useMemo(() => {
    const versions = getVersionsToDisplay(version, allVersions);
    return variant === 'RHS' ? getVersionsToCompareAgainst(versions) : versions;
  }, [allVersions, variant, version, getVersionsToCompareAgainst]);

  const isCompareVersionsDisabled = useMemo(() => {
    return versionsToDisplay ? versionsToDisplay.length === 0 : false;
  }, [versionsToDisplay]);

  const versionOptions = useMemo(() => {
    const options = sortVersions(versionsToDisplay)
      .reverse()
      .map((ver) => ({
        label:
          ver.version !== latestVersion
            ? t('versions.version', { version: ver.version })
            : t('versions.versionLatest', { version: ver.version }),
        id: ver.version,
        'data-cy': `${
          variant === 'RHS' ? 'compare' : 'version'
        }-select-option-${ver.version}`,
      }));

    return options;
  }, [latestVersion, versionsToDisplay, variant, t]);

  const label = useMemo(() => {
    if (isCompareVersionsDisabled && !allVersions?.length) {
      return t('versions.compareAgainst');
    }

    let versionLabel;
    if (sortedVersions.length === 1 && version === latestVersion) {
      versionLabel = t('versions.versionLatest', { version });
    } else {
      if (variant === 'LHS') {
        if (sortedVersions.length === 0 || version === '') {
          return t('versions.selectVersion');
        }

        if (sortedVersions.length > 1) {
          versionLabel =
            version === latestVersion
              ? t('versions.versionLatest', { version })
              : t('versions.version', { version });
        } else {
          versionLabel =
            version === latestVersion
              ? t('versions.versionLatest', { version })
              : `${version}`;
        }
      } else {
        if (sortedVersions.length === 0 || version === '') {
          return t('versions.compareAgainst');
        }

        versionLabel =
          version === latestVersion
            ? t('versions.versionLatest', { version })
            : `${version}`;
      }
    }
    return variant === 'LHS'
      ? versionLabel
      : t('versions.compareAgainstVersion', {
          versionLabel: versionLabel?.toLowerCase(),
        });
  }, [
    allVersions?.length,
    version,
    sortedVersions,
    variant,
    isCompareVersionsDisabled,
    latestVersion,
    t,
  ]);

  if (variant !== 'LHS' && variant !== 'RHS') {
    throw new Error(
      `Unsupported variant type: ${variant} must be one of LHS or RHS`,
    );
  }

  return (
    <QMenuButton
      isDisabled={isCompareVersionsDisabled}
      variant="ghost"
      items={versionOptions}
      onItemClick={(option) => option && handleOptionChange(option.id)}
      buttonLabel={label as string}
      data-cy={
        variant === 'RHS' ? 'compare-against-button' : 'version-select-button'
      }
    />
  );
};
